import { useCallback, useMemo, useSyncExternalStore } from 'react';

export type LiveQuote = {
  contract_key: string;
  symbol: string;
  local_symbol?: string | null;
  sec_type?: string | null;
  con_id?: number | null;
  price: number;
  last?: number | null;
  bid?: number | null;
  ask?: number | null;
  market_data_type?: number | null;
  data_status: string;
  delayed: boolean;
  frozen: boolean;
  source: string;
  exchange_time?: string | null;
  received_at: string;
  stale_after_seconds?: number;
  stale: boolean;
  sequence: number;
};

export type QuoteConnectionStatus = 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED';
type Listener = () => void;

const normalize = (identifier: string) => identifier.trim().toUpperCase();

class QuoteStore {
  private socket: WebSocket | null = null;
  private token: string | null = null;
  private sessionKey: string | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private disposed = true;
  private quotes = new Map<string, LiveQuote>();
  private aliasToKey = new Map<string, string>();
  private listeners = new Map<string, Set<Listener>>();
  private references = new Map<string, number>();
  private statusListeners = new Set<Listener>();
  private status: QuoteConnectionStatus = 'DISCONNECTED';
  private brokerConnected = false;
  private version = 0;
  private pendingListeners = new Set<Listener>();
  private notificationTimer: number | null = null;
  private lastMessageAt = 0;

  constructor() {
    if (typeof window !== 'undefined') {
      const handleWake = () => this.handleWakeup();
      document.addEventListener('visibilitychange', handleWake);
      window.addEventListener('focus', handleWake);
      window.addEventListener('online', handleWake);
    }
  }

  private handleWakeup() {
    if (this.disposed || !this.token) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }

    const now = Date.now();
    // A socket claiming to be OPEN with no messages for > 15s is likely a dead zombie connection frozen by the mobile OS.
    const isZombie = this.socket?.readyState === WebSocket.OPEN && this.lastMessageAt > 0 && (now - this.lastMessageAt > 15000);
    const isSocketDown = !this.socket || this.socket.readyState === WebSocket.CLOSING || this.socket.readyState === WebSocket.CLOSED || isZombie;

    if (isSocketDown) {
      if (this.reconnectTimer !== null) {
        window.clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      this.reconnectAttempts = 0;
      if (this.socket) {
        try {
          this.socket.onclose = null;
          this.socket.onerror = null;
          this.socket.onmessage = null;
          this.socket.close();
        } catch {
          // ignore
        }
        this.socket = null;
      }
      this.connect();
    } else if (this.socket?.readyState === WebSocket.OPEN) {
      if (this.references.size > 0) {
        this.send('subscribe', [...this.references.keys()]);
      }
      this.refreshStaleness();
    }
  }

  configure(token: string | null, sessionKey: string | null) {
    if (this.token === token && this.sessionKey === sessionKey) return;
    this.disconnect(true);
    this.token = token;
    this.sessionKey = sessionKey;
    if (token && sessionKey) {
      this.disposed = false;
      this.connect();
    }
  }

  subscribe(identifier: string, listener: Listener) {
    const alias = normalize(identifier);
    if (!alias) return () => undefined;
    const listeners = this.listeners.get(alias) ?? new Set<Listener>();
    listeners.add(listener);
    this.listeners.set(alias, listeners);
    const count = (this.references.get(alias) ?? 0) + 1;
    this.references.set(alias, count);
    if (count === 1 && this.socket?.readyState === WebSocket.OPEN) {
      this.send('subscribe', [identifier]);
    }
    return () => {
      const current = this.listeners.get(alias);
      current?.delete(listener);
      if (current?.size === 0) this.listeners.delete(alias);
      const nextCount = Math.max(0, (this.references.get(alias) ?? 1) - 1);
      if (nextCount === 0) {
        this.references.delete(alias);
        if (this.socket?.readyState === WebSocket.OPEN) this.send('unsubscribe', [identifier]);
      } else {
        this.references.set(alias, nextCount);
      }
    };
  }

  getQuote(identifier: string): LiveQuote | null {
    const alias = normalize(identifier);
    const key = this.aliasToKey.get(alias) ?? alias.toLowerCase();
    return this.quotes.get(key) ?? this.quotes.get(alias) ?? null;
  }

  subscribeStatus(listener: Listener) {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  getStatus = () => this.status;
  getVersion = () => this.version;

  clear() {
    if (this.notificationTimer !== null) window.clearTimeout(this.notificationTimer);
    this.notificationTimer = null;
    this.pendingListeners.clear();
    this.quotes.clear();
    this.aliasToKey.clear();
    this.version += 1;
    this.emitAll();
  }

  private connect() {
    if (this.disposed || !this.token) return;
    this.setStatus('CONNECTING');
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.socket = new WebSocket(
      `${protocol}//${window.location.host}/api/v1/market/ws`,
      ['access_token', this.token],
    );
    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.lastMessageAt = Date.now();
      // The browser socket is only the transport. Wait for the server's broker
      // status before presenting retained quotes as live market data.
      this.brokerConnected = false;
      this.setStatus('CONNECTING');
      this.send('subscribe', [...this.references.keys()]);
    };
    this.socket.onmessage = (event) => this.handleMessage(event.data);
    this.socket.onclose = (event) => {
      this.socket = null;
      this.brokerConnected = false;
      this.setStatus('DISCONNECTED');
      this.refreshStaleness();
      if (this.disposed || event.code === 4401 || event.code === 4403) return;
      this.reconnectAttempts += 1;
      const isHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      const delay = isHidden ? 30000 : Math.min(15000, 500 * 2 ** Math.min(this.reconnectAttempts, 5));
      this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
    };
  }

  private handleMessage(raw: string) {
    this.lastMessageAt = Date.now();
    let message: any;
    try {
      message = JSON.parse(raw);
    } catch {
      return;
    }
    if (message.type === 'connection.status' || message.type === 'heartbeat') {
      const connected = message.status === 'CONNECTED';
      if (connected && !this.brokerConnected) {
        this.send('subscribe', [...this.references.keys()]);
      }
      this.brokerConnected = connected;
      this.setStatus(connected ? 'CONNECTED' : 'DISCONNECTED');
      this.refreshStaleness();
      return;
    }
    if (message.type !== 'quote.updated' || !message.quote?.contract_key) return;
    const incomingQuote = message.quote as LiveQuote;
    const quote = this.brokerConnected
      ? incomingQuote
      : { ...incomingQuote, stale: true };
    const previous = this.quotes.get(quote.contract_key);
    if (previous && previous.sequence >= quote.sequence) return;
    this.quotes.set(quote.contract_key, quote);
    this.version += 1;
    const aliases = [quote.contract_key, quote.symbol, quote.local_symbol, quote.con_id ? `conid:${quote.con_id}` : null]
      .filter(Boolean)
      .map((value) => normalize(String(value)));
    for (const alias of aliases) this.aliasToKey.set(alias, quote.contract_key);
    const notified = new Set<Listener>();
    for (const alias of aliases) {
      for (const listener of this.listeners.get(alias) ?? []) notified.add(listener);
    }
    this.scheduleListeners(notified);
  }

  private send(action: 'subscribe' | 'unsubscribe', contracts: string[]) {
    if (contracts.length && this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ action, contracts }));
    }
  }

  private refreshStaleness() {
    let changed = false;
    const now = Date.now();
    for (const [key, quote] of this.quotes) {
      const timestamp = quote.exchange_time || quote.received_at;
      const observedAt = timestamp ? Date.parse(timestamp) : Number.NaN;
      const stale = !this.brokerConnected
        || quote.stale
        || !Number.isFinite(observedAt)
        || (now - observedAt) / 1000 > (quote.stale_after_seconds ?? 60);
      if (stale !== quote.stale) {
        this.quotes.set(key, { ...quote, stale });
        changed = true;
      }
    }
    if (changed) {
      this.version += 1;
      this.emitAll();
    }
  }

  private disconnect(clearQuotes: boolean) {
    this.disposed = true;
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    if (this.socket) {
      // Prevent a late close/message from an old tenant socket scheduling a
      // reconnect after a managed-user switch.
      this.socket.onclose = null;
      this.socket.onmessage = null;
      this.socket.close();
    }
    this.socket = null;
    this.brokerConnected = false;
    this.setStatus('DISCONNECTED');
    if (clearQuotes) this.clear();
  }

  private setStatus(status: QuoteConnectionStatus) {
    if (this.status === status) return;
    this.status = status;
    for (const listener of this.statusListeners) listener();
  }

  private emitAll() {
    const listeners = new Set([...this.listeners.values()].flatMap((set) => [...set]));
    for (const listener of listeners) listener();
  }

  private scheduleListeners(listeners: Iterable<Listener>) {
    for (const listener of listeners) this.pendingListeners.add(listener);
    if (this.notificationTimer !== null) return;

    const delay = document.visibilityState === 'hidden' ? 500 : 100;
    this.notificationTimer = window.setTimeout(() => {
      this.notificationTimer = null;
      const pending = [...this.pendingListeners];
      this.pendingListeners.clear();
      for (const listener of pending) listener();
    }, delay);
  }
}

export const quoteStore = new QuoteStore();

export const useQuote = (identifier: string | null | undefined) => {
  const target = identifier ? normalize(identifier) : '';
  const subscribe = useCallback(
    (listener: Listener) => target ? quoteStore.subscribe(target, listener) : () => undefined,
    [target],
  );
  const getSnapshot = useCallback(
    () => target ? quoteStore.getQuote(target) : null,
    [target],
  );
  return useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => null,
  );
};

export const useQuoteConnectionStatus = () =>
  useSyncExternalStore(
    (listener) => quoteStore.subscribeStatus(listener),
    quoteStore.getStatus,
    () => 'DISCONNECTED' as const,
  );

export const useQuotes = (identifiers: Array<string | null | undefined>) => {
  const subscriptionKey = identifiers
    .filter(Boolean)
    .map((value) => normalize(String(value)))
    .sort()
    .join('\u0000');
  const targets = useMemo(
    () => subscriptionKey ? subscriptionKey.split('\u0000') : [],
    [subscriptionKey],
  );
  const subscribe = useCallback(
    (listener: Listener) => {
      const unsubscribe = targets.map((target) => quoteStore.subscribe(target, listener));
      return () => unsubscribe.forEach((dispose) => dispose());
    },
    [targets],
  );
  useSyncExternalStore(
    subscribe,
    quoteStore.getVersion,
    () => 0,
  );
  return Object.fromEntries(targets.map((target) => [target, quoteStore.getQuote(target)]));
};
